// CriticalNavigationCSS.js
// Provides critical inline CSS for navigation that works across ALL scenes
// Ensures buttons are always visible and clickable regardless of external CSS loading

/**
 * Add critical navigation CSS to ensure buttons always work
 * This CSS is applied inline and uses !important to override any conflicts
 * Works with any overlay ID that contains navigation elements
 */
export function addCriticalNavigationCSS() {
    // Check if already added
    if (document.getElementById('critical-navigation-css')) {
        console.log('🎨 Critical navigation CSS already loaded');
        return;
    }
    
    const criticalCSS = document.createElement('style');
    criticalCSS.id = 'critical-navigation-css';
    criticalCSS.textContent = `
        /* ========================================
           CRITICAL NAVIGATION CSS
           Ensures navigation works across all scenes
           regardless of external CSS loading
           ======================================== */
        
        /* Overlay containers - apply to all possible overlay IDs */
        #auth-menu-dom-overlay,
        #notifications-scene-overlay,
        #admin-panel-scene-overlay,
        #profile-scene-overlay,
        #game-center-overlay,
        #analytics-scene-overlay,
        #hall-of-fame-overlay {
            display: block !important;
            visibility: visible !important;
            opacity: 1 !important;
            pointer-events: auto !important;
            position: fixed !important;
            top: 0 !important;
            left: 0 !important;
            width: 100vw !important;
            height: 100vh !important;
            z-index: 10000 !important;
            overflow: hidden !important;
            font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif !important;
        }
        
        /* App container layout */
        #auth-menu-dom-overlay .app-container,
        #notifications-scene-overlay .app-container,
        #admin-panel-scene-overlay .app-container,
        #profile-scene-overlay .app-container,
        #game-center-overlay .app-container,
        #analytics-scene-overlay .app-container,
        #hall-of-fame-overlay .app-container {
            display: flex !important;
            height: 100vh !important;
            position: relative !important;
        }
        
        /* Sidebar navigation - CRITICAL for all scenes */
        #auth-menu-dom-overlay .sidebar,
        #notifications-scene-overlay .sidebar,
        #admin-panel-scene-overlay .sidebar,
        #profile-scene-overlay .sidebar,
        #game-center-overlay .sidebar,
        #analytics-scene-overlay .sidebar,
        #hall-of-fame-overlay .sidebar {
            display: flex !important;
            flex-direction: column !important;
            width: 280px !important;
            min-width: 280px !important;
            max-width: 280px !important;
            visibility: visible !important;
            pointer-events: auto !important;
            background: rgba(30, 41, 59, 0.95) !important;
            border-right: 1px solid rgba(255, 255, 255, 0.1) !important;
            z-index: 1 !important;
        }
        
        /* Navigation menu */
        #auth-menu-dom-overlay .nav-menu,
        #notifications-scene-overlay .nav-menu,
        #admin-panel-scene-overlay .nav-menu,
        #profile-scene-overlay .nav-menu,
        #game-center-overlay .nav-menu,
        #analytics-scene-overlay .nav-menu,
        #hall-of-fame-overlay .nav-menu {
            display: block !important;
            visibility: visible !important;
            pointer-events: auto !important;
            flex: 1 !important;
            padding: 16px 0 !important;
            overflow-y: auto !important;
        }
        
        /* Navigation items - MUST be clickable */
        #auth-menu-dom-overlay .nav-item,
        #notifications-scene-overlay .nav-item,
        #admin-panel-scene-overlay .nav-item,
        #profile-scene-overlay .nav-item,
        #game-center-overlay .nav-item,
        #analytics-scene-overlay .nav-item,
        #hall-of-fame-overlay .nav-item {
            display: flex !important;
            align-items: center !important;
            gap: 12px !important;
            visibility: visible !important;
            pointer-events: auto !important;
            cursor: pointer !important;
            padding: 12px 24px !important;
            color: rgba(255, 255, 255, 0.7) !important;
            text-decoration: none !important;
            transition: all 0.3s ease !important;
            border: none !important;
            background: transparent !important;
            width: 100% !important;
            text-align: left !important;
        }
        
        /* Navigation item hover state */
        #auth-menu-dom-overlay .nav-item:hover,
        #notifications-scene-overlay .nav-item:hover,
        #admin-panel-scene-overlay .nav-item:hover,
        #profile-scene-overlay .nav-item:hover,
        #game-center-overlay .nav-item:hover,
        #analytics-scene-overlay .nav-item:hover,
        #hall-of-fame-overlay .nav-item:hover {
            background: rgba(255, 255, 255, 0.05) !important;
            color: rgba(255, 255, 255, 1) !important;
        }
        
        /* Navigation item active state */
        #auth-menu-dom-overlay .nav-item.active,
        #notifications-scene-overlay .nav-item.active,
        #admin-panel-scene-overlay .nav-item.active,
        #profile-scene-overlay .nav-item.active,
        #game-center-overlay .nav-item.active,
        #analytics-scene-overlay .nav-item.active,
        #hall-of-fame-overlay .nav-item.active {
            background: linear-gradient(135deg, rgba(0, 212, 255, 0.1) 0%, rgba(14, 165, 233, 0.1) 100%) !important;
            color: #00d4ff !important;
            border-left: 3px solid #00d4ff !important;
        }
        
        /* Main content area */
        #auth-menu-dom-overlay .main-content,
        #notifications-scene-overlay .main-content,
        #admin-panel-scene-overlay .main-content,
        #profile-scene-overlay .main-content,
        #game-center-overlay .main-content,
        #analytics-scene-overlay .main-content,
        #hall-of-fame-overlay .main-content {
            display: flex !important;
            flex-direction: column !important;
            flex: 1 !important;
            visibility: visible !important;
            pointer-events: auto !important;
            overflow: hidden !important;
        }
        
        /* Header buttons */
        #auth-menu-dom-overlay .header-btn,
        #notifications-scene-overlay .header-btn,
        #admin-panel-scene-overlay .header-btn,
        #profile-scene-overlay .header-btn,
        #game-center-overlay .header-btn,
        #analytics-scene-overlay .header-btn,
        #hall-of-fame-overlay .header-btn {
            display: flex !important;
            align-items: center !important;
            justify-content: center !important;
            visibility: visible !important;
            pointer-events: auto !important;
            cursor: pointer !important;
            width: 44px !important;
            height: 44px !important;
            border-radius: 12px !important;
            border: 1px solid rgba(255, 255, 255, 0.1) !important;
            background: transparent !important;
            color: rgba(255, 255, 255, 0.7) !important;
            transition: all 0.3s ease !important;
        }
        
        #auth-menu-dom-overlay .header-btn:hover,
        #notifications-scene-overlay .header-btn:hover,
        #admin-panel-scene-overlay .header-btn:hover,
        #profile-scene-overlay .header-btn:hover,
        #game-center-overlay .header-btn:hover,
        #analytics-scene-overlay .header-btn:hover,
        #hall-of-fame-overlay .header-btn:hover {
            background: rgba(255, 255, 255, 0.1) !important;
            border-color: #00d4ff !important;
        }
        
        /* Game cards/tiles */
        #auth-menu-dom-overlay .game-card,
        #game-center-overlay .game-card {
            display: flex !important;
            visibility: visible !important;
            pointer-events: auto !important;
            cursor: pointer !important;
        }
        
        /* Ensure icons are visible */
        .nav-icon,
        .nav-label {
            display: inline-block !important;
            visibility: visible !important;
        }
        
        /* Debug: Log when critical CSS is applied */
        body::after {
            content: "Critical Navigation CSS Active" !important;
            position: fixed !important;
            bottom: 0 !important;
            right: 0 !important;
            background: rgba(0, 212, 255, 0.1) !important;
            color: #00d4ff !important;
            padding: 4px 8px !important;
            font-size: 10px !important;
            z-index: 999999 !important;
            pointer-events: none !important;
        }
    `;
    
    document.head.appendChild(criticalCSS);
    console.log('🎨 Critical navigation CSS added globally - covers ALL scenes');
    
    return criticalCSS;
}

/**
 * Load desktop template CSS without query parameters
 * @param {string} cssId - ID for the link element
 * @returns {Promise<void>}
 */
export function loadDesktopTemplateCSS(cssId = 'desktop-executive-dashboard-exact-css') {
    return new Promise((resolve, reject) => {
        // Check if already loaded
        if (document.getElementById(cssId)) {
            console.log(`🎨 CSS already loaded: ${cssId}`);
            resolve();
            return;
        }
        
        console.log(`🎨 Loading ${cssId}...`);
        
        const link = document.createElement('link');
        link.id = cssId;
        link.rel = 'stylesheet';
        link.href = '/desktop-executive-dashboard-exact.css'; // NO query parameter
        
        link.onload = () => {
            console.log(`✅ CSS loaded successfully: ${cssId}`);
            resolve();
        };
        
        link.onerror = (error) => {
            console.error(`❌ CSS failed to load: ${cssId}`, error);
            // Don't reject - critical CSS ensures functionality anyway
            resolve();
        };
        
        document.head.appendChild(link);
    });
}

/**
 * Get debug info about navigation elements
 * @param {string} overlayId - Overlay ID to check
 * @returns {object} Debug information
 */
export function debugNavigationElements(overlayId = 'auth-menu-dom-overlay') {
    const overlay = document.getElementById(overlayId);
    if (!overlay) {
        return { error: `Overlay ${overlayId} not found` };
    }
    
    const navItems = overlay.querySelectorAll('.nav-item[data-action], .nav-item[data-game]');
    const debugInfo = {
        overlayId,
        overlayVisible: window.getComputedStyle(overlay).display !== 'none',
        totalNavItems: navItems.length,
        navItems: []
    };
    
    navItems.forEach((item, index) => {
        const styles = window.getComputedStyle(item);
        debugInfo.navItems.push({
            index,
            action: item.dataset.action || item.dataset.game,
            display: styles.display,
            visibility: styles.visibility,
            pointerEvents: styles.pointerEvents,
            opacity: styles.opacity,
            cursor: styles.cursor
        });
    });
    
    return debugInfo;
}

// Make debug function globally available
if (typeof window !== 'undefined') {
    window.debugNavigation = debugNavigationElements;
    console.log('🔍 Navigation debug available: window.debugNavigation("overlay-id")');
}

export default {
    addCriticalNavigationCSS,
    loadDesktopTemplateCSS,
    debugNavigationElements
};

